import { HStack, FormLabel, Input, useToast, Spacer } from "@chakra-ui/react";
import { useState } from "react";
import {
  convertPlaceDetailResultToGeoJSON,
  FeatureCollection,
} from "../../../components/mapcomponent/generic";
import PageComponent from "../../../components/PageComponent";
import PlacesDetailDocs from "../../../components/api-docs/PlacesDetailDocs";

export default function PlaceDetail() {
  const [apiKey, setApiKey] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [fields, setFields] = useState("*");

  const [results, setResults] = useState<{
    apiResponse: string;
    geoJSON: FeatureCollection;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  const handleCopy = () => {
    if (!results || !results.apiResponse) return;

    navigator.clipboard.writeText(results.apiResponse);
    toast({
      title: "Success!",
      description: "API response successfully copied to your clipboard",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  async function handleSubmit() {
    try {
      setIsLoading(true);

      const response = await fetch(
        `${process.env.REACT_APP_V1_API_SERVER_ENDPOINT}/places/detail/${encodeURIComponent(
          placeId,
        )}?fields=${encodeURIComponent(fields || "*")}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": apiKey,
          },
        },
      );

      if (response.status === 401) {
        setIsLoading(false);
        setResults(null);
        toast({
          title: "Error!",
          description: "Please provide a valid API key",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (response.status === 404) {
        setIsLoading(false);
        setResults(null);
        toast({
          title: "Not found!",
          description: "No place exists with that place ID",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      if (response.status !== 200) {
        setIsLoading(false);
        setResults(null);
        toast({
          title: "Error!",
          description: "There was an error fetching data",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const data = await response.json();

      if (!data || !data.id) {
        setIsLoading(false);
        setResults(null);
        toast({
          title: "No results!",
          description: "The place detail request did not return a place",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      toast({
        title: "Success!",
        description: "Place detail successfully retrieved",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      setResults({
        apiResponse: JSON.stringify(data, null, 2),
        geoJSON: convertPlaceDetailResultToGeoJSON(data),
      });
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setResults(null);
      console.log(error);
      toast({
        title: "Error!",
        description: "There was an error fetching data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }

  return (
    <PageComponent
      navItemsV1={[
        { title: "Places Detail API", nav: "api" },
        { title: "Try out the API", nav: "playground" },
        { title: "API Documentation", nav: "documentation" },
        { title: "Base URL", nav: "baseurl" },
        { title: "Endpoint", nav: "endpoint" },
        { title: "Authentication", nav: "authentication" },
        { title: "Headers", nav: "headers" },
        { title: "Path Parameters", nav: "pathparameters" },
        { title: "Query Parameters", nav: "queryparameters" },
        { title: "Request Examples", nav: "requestexamples" },
        { title: "Response Body", nav: "responsebody" },
        { title: "Response Field Reference", nav: "responsefields" },
        { title: "Example Requests", nav: "examplerequests" },
        { title: "Status Codes", nav: "statuscodes" },
        { title: "Notes", nav: "notes" },
      ]}
      handleCopy={handleCopy}
      handleSubmit={handleSubmit}
      isLoading={isLoading}
      isDisabled={!placeId || !apiKey}
      result={results}
      documentationComponent={<PlacesDetailDocs />}
      formComponent={
        <>
          <HStack>
            <FormLabel pb={0} mb={0}>
              API Key
            </FormLabel>{" "}
            <Spacer />
            <Input
              isRequired
              type="text"
              w="3fr"
              placeholder="Enter API key"
              borderColor={"#bcbcbc"}
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
          </HStack>
          <HStack>
            <FormLabel pb={0} mb={0} whiteSpace={"nowrap"}>
              Place ID
            </FormLabel>{" "}
            <Spacer />
            <Input
              isRequired
              type="text"
              w="3fr"
              placeholder="019f1775-dc1f-7707-938f-ff043b1702be"
              borderColor={"#bcbcbc"}
              value={placeId}
              onChange={(e) => setPlaceId(e.target.value)}
            />
          </HStack>
          <HStack>
            <FormLabel pb={0} mb={0}>
              Fields
            </FormLabel>{" "}
            <Spacer />
            <Input
              type="text"
              w="3fr"
              placeholder="*"
              borderColor={"#bcbcbc"}
              value={fields}
              onChange={(e) => setFields(e.target.value)}
            />
          </HStack>
        </>
      }
      headerTitle={"Places Detail"}
      headerPath={"v1/places/detail/{place_id}"}
      headerDescription={
        "Retrieve full details for a single place using its place ID. Returns the place's display names, structured address components and geographic location."
      }
    />
  );
}
